import { useState, useMemo, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import CustomerCard from './components/CustomerCard';
import CustomerFormModal from './components/CustomerFormModal';
import CustomerExportButton from './components/CustomerExportButton';
import Pagination from './components/Pagination';
import { useCustomers } from './hooks/useCustomers';
import type { CustomerWithTags } from '../../types';

type Customer = CustomerWithTags;

export default function CustomersPage() {
  const { customers, loading, error, refresh } = useCustomers();
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterTypeId, setFilterTypeId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Collect unique types from loaded customers
  const uniqueTypes = useMemo(() => {
    const map = new Map<string, string>();
    customers.forEach((c) => {
      if (c.customerTypeId && c.customerTypeName) {
        map.set(c.customerTypeId, c.customerTypeName);
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [customers]);

  const filtered: Customer[] = useMemo(() => {
    return customers.filter((c) => {
      const q = search.toLowerCase();
      const matchSearch =
        !q ||
        (c.fullName ?? '').toLowerCase().includes(q) ||
        (c.phone ?? '').includes(q) ||
        (c.companyName ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q);
      const matchType = !filterTypeId || c.customerTypeId === filterTypeId;
      return matchSearch && matchType;
    });
  }, [customers, search, filterTypeId]);

  // Reset to page 1 when filter/search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, filterTypeId]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  const paginated = useMemo(() => {
    if (isMobile) return filtered; // mobile: show all
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize, isMobile]);

  return (
    <div className="min-h-screen bg-[#f8fafa]">
      <Navbar />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">客户管理</h1>
            <p className="text-sm text-gray-400 mt-1">
              管理你的所有客户信息，共{' '}
              <span className="text-teal-600 font-medium">{customers.length}</span> 位客户
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CustomerExportButton customers={filtered} disabled={loading} />

            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-user-add-line" />
              </div>
              <span>新建客户</span>
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 md:gap-3 mb-5 md:mb-6">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
              <i className="ri-search-line text-gray-400 text-sm" />
            </div>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索姓名、手机、公司..."
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-circle-fill text-sm" />
              </button>
            )}
          </div>

          {/* Type filter - horizontal scroll on mobile */}
          {uniqueTypes.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-0.5 sm:pb-0 scrollbar-hide">
              <button
                type="button"
                onClick={() => setFilterTypeId('')}
                className={`px-3 py-2 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0 ${
                  !filterTypeId
                    ? 'bg-teal-500 text-white'
                    : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-200'
                }`}
              >
                全部
              </button>
              {uniqueTypes.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setFilterTypeId(filterTypeId === t.id ? '' : t.id)}
                  className={`px-3 py-2 text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap shrink-0 ${
                    filterTypeId === t.id
                      ? 'bg-teal-500 text-white'
                      : 'bg-white text-gray-500 border border-gray-200 hover:border-teal-200'
                  }`}
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}

          {filtered.length !== customers.length && (
            <span className="text-xs text-gray-400 sm:ml-auto shrink-0">
              显示 {filtered.length} / {customers.length} 位
            </span>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 bg-gray-100 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mb-3">
              <i className="ri-error-warning-line text-red-400 text-2xl" />
            </div>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <button
              type="button"
              onClick={refresh}
              className="px-4 py-2 text-sm text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 cursor-pointer transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-2xl mb-4">
              <i className="ri-user-3-line text-teal-300 text-3xl" />
            </div>
            <p className="text-base font-medium text-gray-600 mb-1">
              {search || filterTypeId ? '没有匹配的客户' : '还没有客户'}
            </p>
            <p className="text-sm text-gray-400 mb-6">
              {search || filterTypeId ? '尝试调整搜索条件' : '点击右上角「新建客户」添加第一位客户'}
            </p>
            {!search && !filterTypeId && (
              <button
                type="button"
                onClick={() => setShowModal(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-user-add-line" />
                </div>
                新建客户
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {paginated.map((customer) => (
                <CustomerCard key={customer.id} customer={customer} />
              ))}
            </div>

            {/* Pagination: desktop only */}
            {!isMobile && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filtered.length}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
              />
            )}

            {/* Mobile scroll end hint */}
            {isMobile && filtered.length > 0 && (
              <div className="text-center py-6 text-xs text-gray-400">
                共 {filtered.length} 位客户，已全部加载
              </div>
            )}
          </>
        )}
      </div>

      <CustomerFormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        onSuccess={refresh}
      />
    </div>
  );
}
